import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import AppButton from '@/components/ui/AppButton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { toast } from '../lib/swal';
import { 
  Trophy, CheckCircle, Play, XCircle, Settings, Eye, EyeOff, 
  Image as ImageIcon, Trash2, Edit2, Activity, Info, Video,
  Plus, Save, ChevronRight, DollarSign, Users, Film, Layout,
  Type, Palette, Share2, UploadCloud, Youtube, Music, Trash, ArrowRight,
  Monitor, MessageSquare, Phone, CreditCard, Shuffle, Hash
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { formatCurrency, formatDateTime, cn, getMediaUrl } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RaffleCard = ({ 
  sorteo, 
  publicarSorteo, 
  editarSorteo, 
  eliminarSorteo, 
  pausarDespausarVentas, 
  abrirConfigOperativa, 
  ocultarSorteo, 
  finalizarSorteo, 
  setSorteoExpandido,
  sorteoExpandido,
  fetchData 
}) => {
  // Estados locales para edición de medios (para no contaminar el dashboard global)
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [showMediosModal, setShowMediosModal] = useState(false);
  const [showPaquetesModal, setShowPaquetesModal] = useState(false);
  const [isSavingPromo, setIsSavingPromo] = useState(false);
  const [isSavingPaquetes, setIsSavingPaquetes] = useState(false);
  
  // Estado para paquetes (copia local para edición)
  const [paquetesEdit, setPaquetesEdit] = useState(sorteo.paquetes || []);
  const [nuevoPaquete, setNuevoPaquete] = useState({ 
    cantidad: 10, 
    boletos_gratis: 0, 
    descuento: 0, 
    etiqueta: '', 
    activo: true 
  });
  
  // Estado para la nueva sección promocional/publicitaria
  const [promoConfig, setPromoConfig] = useState({
    titulo: sorteo.promocion?.titulo || sorteo.titulo || "",
    descripcion: sorteo.promocion?.descripcion || sorteo.descripcion || "",
    texto_promocional: sorteo.promocion?.texto_promocional || "",
    texto_apoyo: sorteo.promocion?.texto_apoyo || "",
    background: sorteo.promocion?.background || { 
      tipo: 'color', 
      valor: '#ffffff', 
      overlay_opacity: 0.5, 
      overlay_color: '#000000' 
    },
    multimedia: sorteo.promocion?.multimedia || []
  });

  // Al abrir el modal, sincronizar con los datos actuales: prioridad promocion > base sorteo
  useEffect(() => {
    if (showMediosModal) {
      setPromoConfig({
        titulo: sorteo.promocion?.titulo || sorteo.titulo || "",
        descripcion: sorteo.promocion?.descripcion || sorteo.descripcion || "",
        texto_promocional: sorteo.promocion?.texto_promocional || "",
        texto_apoyo: sorteo.promocion?.texto_apoyo || "",
        background: sorteo.promocion?.background || { 
          tipo: 'color', 
          valor: '#ffffff', 
          overlay_opacity: 0.5, 
          overlay_color: '#000000' 
        },
        multimedia: sorteo.promocion?.multimedia || []
      });
    }
  }, [showMediosModal, sorteo]);
  
  // Estados para edición de imágenes/videos (legacy del carrusel y premios)
  const [imagenesTemp, setImagenesTemp] = useState({});
  const [premiosImagenesTemp, setPremiosImagenesTemp] = useState({});

  // getMediaUrl is now imported from ../lib/utils

  const getYoutubePreview = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:v\/|u\/\w\/|embed\/|watch\?v=))([^#&?]*)/);
    const vidId = match?.[1];
    return vidId ? `https://img.youtube.com/vi/${vidId}/mqdefault.jpg` : null;
  };

  const handlePrizeFileUpload = async (index, etapaIndex = null, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('premio_index', index);
    if (etapaIndex !== null) formData.append('etapa_index', etapaIndex);

    try {
      toast.loading('Subiendo imagen de premio...');
      await axios.post(`${API}/admin/sorteo/${sorteo.id}/subir-imagen-premio`, formData, {
        
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.dismiss();
      toast.success('Imagen de premio actualizada');
      fetchData();
    } catch (err) {
      toast.dismiss();
      toast.error('Error al subir imagen');
    }
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      draft: { class: 'border-slate-500/20 bg-slate-500/10 text-slate-500', label: 'Borrador', icon: Edit2 },
      published: { class: 'status-badge-published', label: 'Publicado', icon: CheckCircle },
      waiting: { class: 'border-yellow-500/20 bg-yellow-500/10 text-yellow-500', label: 'En Espera', icon: Activity },
      live: { class: 'border-red-500/20 bg-red-500/10 text-red-500', label: 'En Vivo', icon: Trophy },
      completed: { class: 'border-blue-500/20 bg-blue-500/10 text-blue-500', label: 'Completado', icon: CheckCircle },
      pausado: { class: 'border-orange-500/20 bg-orange-500/10 text-orange-500', label: 'Pausado', icon: XCircle },
      ready: { class: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-500', label: 'Por Iniciar', icon: Play },
    };
    return badges[estado] || badges.draft;
  };

  const badge = getEstadoBadge(sorteo.estado);
  const StatusIcon = badge.icon;
  const progreso = sorteo.progreso_porcentaje || 0;

  return (
    <Card className="raffle-card group">
      <CardContent className="p-0">
        <div className="p-6 md:p-8">
          {/* BLOQUE 1: HEADER */}
          <div className="raffle-header">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h3 className="raffle-title">{sorteo.titulo}</h3>
                {sorteo.ventas_pausadas && (
                  <Badge variant="outline" className="border-orange-500/50 bg-orange-500/10 text-orange-500 flex items-center gap-1 animate-pulse">
                    <XCircle className="w-3 h-3" />
                    Ventas Pausadas
                  </Badge>
                )}
                <div className={`status-badge ${badge.class}`}>
                  <StatusIcon className="w-3 h-3" />
                  {badge.label}
                </div>
              </div>
              <p className="raffle-desc">{sorteo.descripcion}</p>
            </div>
            
            <div className="flex flex-col items-end shrink-0">
              <span className="metric-label">Precio Boleto</span>
              <div className="raffle-price-tag">
                <span className="text-sm font-medium">$</span>
                {sorteo.precio_boleto}
              </div>
            </div>
          </div>

          {/* BLOQUE 3: MÉTRICAS + BARRA PROGRESO */}
          <div className="space-y-4 mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
              <div className="grid grid-cols-2 sm:flex gap-6 sm:gap-8 w-full sm:w-auto">
                <div className="metric-item">
                  <span className="metric-label">Vendidos</span>
                  <span className="metric-value">{sorteo.cantidad_vendida} <span className="text-muted-foreground font-normal text-sm">/ {sorteo.cantidad_total_boletos}</span></span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Ventas</span>
                  <span className={`text-sm font-bold ${sorteo.ventas_pausadas ? 'text-orange-500' : 'text-green-500'}`}>
                    {sorteo.ventas_pausadas ? 'PAUSADAS' : 'ACTIVAS'}
                  </span>
                </div>
              </div>
              <div className="text-right w-full sm:w-auto flex flex-row-reverse sm:flex-col justify-between sm:justify-end items-center sm:items-end gap-2">
                <span className="text-2xl font-black text-foreground">{progreso.toFixed(1)}%</span>
                <span className="block metric-label">Progreso de Venta</span>
              </div>
            </div>

            <div className="progress-bar-container">
              <div 
                className={`progress-bar-fill ${progreso > 80 ? 'from-green-500 to-emerald-400' : ''}`} 
                style={{ width: `${progreso}%` }}
              />
            </div>
          </div>

          {/* BLOQUE: MÉTRICAS OPERATIVAS (Admin Only) */}
          {sorteo.metrics && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 dark:bg-white/5 rounded-2xl border border-slate-100 dark:border-white/5 mb-6 animate-in fade-in slide-in-from-bottom-2 duration-700 shadow-inner">
              <div className="text-center md:text-left space-y-0.5">
                <span className="block text-2xl font-black text-foreground tracking-tighter">{sorteo.metrics.ordenes_generadas}</span>
                <span className="block text-[10px] font-black uppercase text-muted-foreground tracking-widest">Órdenes</span>
              </div>
              <div className="text-center md:text-left space-y-0.5">
                <span className="block text-2xl font-black text-green-500 tracking-tighter">{sorteo.metrics.compras_finalizadas}</span>
                <span className="block text-[10px] font-black uppercase text-muted-foreground tracking-widest">Finalizadas</span>
              </div>
              <div className="text-center md:text-left space-y-0.5">
                <span className="block text-2xl font-black text-orange-500 tracking-tighter">{sorteo.metrics.compras_pendientes}</span>
                <span className="block text-[10px] font-black uppercase text-muted-foreground tracking-widest">Pendientes</span>
              </div>
              <div className="text-center md:text-left space-y-0.5">
                <div className="flex items-baseline justify-center md:justify-start gap-0.5">
                  <span className="block text-2xl font-black text-blue-500 tracking-tighter">{sorteo.metrics.conversion_rate}</span>
                  <span className="text-sm font-black text-blue-500/70">%</span>
                </div>
                <span className="block text-[10px] font-black uppercase text-muted-foreground tracking-widest">Conversión</span>
              </div>
            </div>
          )}

          {/* BLOQUE 4: ETAPAS */}
          {sorteo.tipo === 'etapas' && sorteo.etapas && sorteo.etapas.length > 0 && (
            <div className="flex gap-2 flex-wrap mb-6">
              {sorteo.etapas.map((etapa) => (
                <div 
                  key={etapa.numero} 
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border transition-all duration-300 ${
                    etapa.completado 
                      ? 'bg-green-500/10 text-green-500 border-green-500/20' 
                      : 'bg-slate-100 dark:bg-white/5 text-muted-foreground border-slate-200 dark:border-white/5'
                  }`}
                >
                  Etapa {etapa.numero} • {etapa.porcentaje}% {etapa.completado ? '✓' : ''}
                </div>
              ))}
            </div>
          )}

          {/* BLOQUE 5: ACCIONES */}
          <div className="raffle-actions">
            {sorteo.estado === 'draft' && (
              <>
                <AppButton variant="primary" size="sm" onClick={() => publicarSorteo(sorteo.id)} icon={CheckCircle}>
                  Publicar
                </AppButton>
                <AppButton variant="secondary" size="sm" onClick={() => editarSorteo(sorteo.id)} icon={Edit2}>
                  Editar
                </AppButton>
                <AppButton 
                  variant="danger" 
                  size="sm" 
                  onClick={() => eliminarSorteo(sorteo.id)} 
                  icon={Trash2}
                  className="bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/20 px-4"
                >
                  Eliminar
                </AppButton>
              </>
            )}

            {(sorteo.estado === 'published' || sorteo.estado === 'activo' || sorteo.estado === 'waiting') && (
              <>
                <AppButton 
                  variant={sorteo.ventas_pausadas ? "success" : "secondary"}
                  size="sm"
                  onClick={() => pausarDespausarVentas(sorteo.id, !sorteo.ventas_pausadas)}
                  icon={sorteo.ventas_pausadas ? Play : XCircle}
                >
                  {sorteo.ventas_pausadas ? 'Reanudar ventas' : 'Pausar ventas'}
                </AppButton>
                <AppButton variant="secondary" size="sm" onClick={() => abrirConfigOperativa(sorteo)} icon={Settings}>
                  Config
                </AppButton>
                <AppButton variant="secondary" size="sm" onClick={() => ocultarSorteo(sorteo.id)} icon={sorteo.oculto ? Eye : EyeOff}>
                  {sorteo.oculto ? 'Mostrar' : 'Ocultar'}
                </AppButton>
                <AppButton 
                  variant="secondary" 
                  size="sm" 
                  onClick={() => setShowMediosModal(true)} 
                  icon={ImageIcon}
                  title="Gestión de imágenes y videos"
                >
                  Medios
                </AppButton>
                <AppButton 
                  variant="secondary" 
                  size="sm" 
                  onClick={() => {
                    setPaquetesEdit(sorteo.paquetes || []);
                    setShowPaquetesModal(true);
                  }} 
                  icon={Plus}
                  className="bg-orange-500/10 text-orange-600 hover:bg-orange-500 hover:text-white border-orange-500/20"
                >
                  Paquetes
                </AppButton>
                <AppButton 
                  variant="secondary" 
                  size="sm" 
                  onClick={async () => {
                    try {
                      const response = await axios.put(`${API}/admin/sorteo/${sorteo.id}/toggle-progreso`);
                      toast.success(response.data.message);
                      fetchData();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Error al cambiar visibilidad");
                    }
                  }} 
                  icon={sorteo.mostrar_progreso === false ? EyeOff : Eye}
                  className={sorteo.mostrar_progreso === false ? "bg-red-500/10 text-red-600 border-red-500/20" : "bg-blue-500/10 text-blue-600 border-blue-500/20"}
                >
                  {sorteo.mostrar_progreso === false ? 'Progreso' : 'Progreso'}
                </AppButton>
                <AppButton 
                  variant="secondary" 
                  size="sm" 
                  onClick={async () => {
                    const isLottery = (sorteo.draw_mode || 'manual_ticket_input') === 'manual_ticket_input';
                    const nextLabel = isLottery ? 'Revelación dígito a dígito' : 'Ingreso de Número (Lotería)';
                    try {
                      await axios.put(`${API}/admin/sorteo/${sorteo.id}/toggle-draw-mode`);
                      toast.success(`Modo cambiado a: ${nextLabel}`);
                      fetchData();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Error al cambiar modo');
                    }
                  }}
                  icon={(sorteo.draw_mode || 'manual_ticket_input') === 'manual_ticket_input' ? Hash : Shuffle}
                  className={(sorteo.draw_mode || 'manual_ticket_input') === 'manual_ticket_input'
                    ? 'bg-violet-500/10 text-violet-600 border-violet-500/20 hover:bg-violet-500 hover:text-white'
                    : 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20 hover:bg-emerald-500 hover:text-white'
                  }
                  title={(sorteo.draw_mode || 'manual_ticket_input') === 'manual_ticket_input' ? 'Modo Actual: Ingreso de Número (Lotería). Click para cambiar.' : 'Modo Actual: Revelación Manual. Click para cambiar.'}
                >
                  {(sorteo.draw_mode || 'manual_ticket_input') === 'manual_ticket_input' ? 'Nº Lotería' : 'Revelar'}
                </AppButton>
                <AppButton 
                  variant="danger" 
                  size="sm" 
                  onClick={() => eliminarSorteo(sorteo.id)} 
                  icon={Trash2}
                  className="bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/20 px-4"
                >
                   {sorteo.cantidad_vendida > 0 ? `Eliminar (${sorteo.cantidad_vendida})` : 'Eliminar'}
                </AppButton>
              </>
            )}


            {(sorteo.estado === 'completed' || sorteo.estado === 'completado') && (
              <div className="flex w-full items-center justify-between">
                <span className="text-xs text-muted-foreground italic">Sorteo finalizado exitosamente</span>
                <AppButton 
                  variant="danger" 
                  size="sm" 
                  onClick={() => eliminarSorteo(sorteo.id)} 
                  icon={Trash2}
                  className="bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/20 px-4"
                >
                  Eliminar Registro
                </AppButton>
              </div>
            )}
          </div>

          {/* GANADORES (si existen) */}
          {/* GANADORES (si existen) */}
          {(sorteo.estado === 'completed' || sorteo.estado === 'completado') && sorteo.ganadores?.length > 0 && (
             <div className="mt-8 pt-6 border-t border-slate-100 dark:border-white/5">
                <div className="flex items-center justify-between mb-5">
                   <h4 className="text-sm font-black text-foreground uppercase tracking-wider flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-yellow-500" /> Ganadores Oficiales
                   </h4>
                   <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20 px-3 py-1 text-[10px] font-bold uppercase">
                      Confirmados
                   </Badge>
                </div>
                
                <div className="grid gap-4">
                   {sorteo.ganadores.map((g, idx) => {
                      const waBody = `¡Hola ${g.nombre}! 👋 Te contacto de WishWay para informarte que has ganado el premio *${g.premio}* 🏆 en el sorteo *${sorteo.titulo}* con el boleto *#${g.numero_boleto?.toString().padStart(4, '0')}*. 🎊 ¡Muchas felicidades! ¿Cómo podemos proceder con la entrega?`;
                      
                      // Formateo para Ecuador: Eliminar no numéricos, quitar el 0 inicial y poner 593
                      let cleanPhone = g.telefono?.replace(/\D/g, '') || '';
                      if (cleanPhone.startsWith('0')) {
                         cleanPhone = '593' + cleanPhone.substring(1);
                      } else if (cleanPhone && !cleanPhone.startsWith('593')) {
                         cleanPhone = '593' + cleanPhone;
                      }
                      
                      const waUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(waBody)}`;

                      return (
                        <div key={idx} className="group/winner relative bg-white dark:bg-black/20 border border-slate-200 dark:border-white/5 rounded-2xl p-5 hover:border-orange-500/30 transition-all duration-300 shadow-sm">
                           <div className="flex flex-col md:flex-row justify-between gap-6">
                              
                              {/* Left side: Main Info */}
                              <div className="flex items-center gap-5">
                                 <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-600 flex items-center justify-center text-white shadow-lg shadow-yellow-500/20">
                                    <span className="text-2xl font-black italic">#{g.numero_boleto?.toString().padStart(2, '0')}</span>
                                 </div>
                                 <div className="space-y-1">
                                    <h5 className="font-black text-lg text-foreground uppercase tracking-tighter leading-none italic">{g.nombre}</h5>
                                    <div className="flex items-center gap-2">
                                       <Badge className="bg-orange-500/10 text-orange-600 text-[9px] font-black uppercase px-2 py-0">{g.premio}</Badge>
                                       <span className="text-[10px] text-muted-foreground uppercase font-bold">• Sorteo #{sorteo.id.substring(sorteo.id.length - 4)}</span>
                                    </div>
                                 </div>
                              </div>

                              {/* Right side: Contact & ID Data */}
                              <div className="grid grid-cols-2 md:flex items-center gap-6">
                                 <div className="space-y-1 text-left md:text-right">
                                    <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest flex items-center gap-1 md:justify-end">
                                       <CreditCard className="w-3 h-3" /> Cédula / ID
                                    </p>
                                    <p className="text-sm font-bold text-foreground">{g.cedula || 'No registrada'}</p>
                                 </div>

                                 <div className="h-10 w-px bg-slate-100 dark:bg-white/5 hidden md:block" />

                                 <div className="space-y-2">
                                    <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest flex items-center gap-1 md:justify-end">
                                       <Phone className="w-3 h-3" /> Contacto Directo
                                    </p>
                                    <a 
                                      href={waUrl} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-xl shadow-lg shadow-green-500/20 transition-all hover:scale-105 active:scale-95"
                                    >
                                       <MessageSquare className="w-4 h-4 fill-current" />
                                       <span className="text-xs font-black uppercase tracking-wider">WhatsApp</span>
                                    </a>
                                 </div>
                              </div>

                           </div>
                        </div>
                      );
                   })}
                </div>
             </div>
          )}

          {/* MODAL DE GESTIÓN DE MEDIOS (PUBLICIDAD & MULTIMEDIA) */}
          <Dialog open={showMediosModal} onOpenChange={setShowMediosModal}>
            <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col p-0 border-none shadow-2xl dark:bg-[#0f1117] bg-white rounded-[2rem]">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-orange-500 via-rose-500 to-orange-500"></div>
              
              <DialogHeader className="px-10 pt-10 pb-6 border-b border-slate-100 dark:border-white/5 bg-slate-50/50 dark:bg-white/5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-5">
                    <div className="w-14 h-14 rounded-2xl bg-orange-500 shadow-xl shadow-orange-500/20 flex items-center justify-center text-white">
                      <Monitor className="w-7 h-7" />
                    </div>
                    <div>
                      <DialogTitle className="text-3xl font-black text-foreground tracking-tight">Campaña Publicitaria</DialogTitle>
                      <DialogDescription className="text-muted-foreground font-medium text-base">
                        Configura la sección promocional para el <span className="text-orange-500 font-bold">Home</span>
                      </DialogDescription>
                    </div>
                  </div>
                  <Badge variant="success" className="px-4 py-1.5 text-xs font-black uppercase ring-4 ring-green-500/10">Editor Activo</Badge>
                </div>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto px-10 py-8 space-y-12 scrollbar-premium">
                
                {/* SECCIÓN 1: TEXTOS DE MARKETING (SYNC) */}
                <div className="space-y-6">
                  <div className="flex items-center gap-3 border-l-4 border-orange-500 pl-4 py-1">
                    <Type className="w-5 h-5 text-orange-500" />
                    <h3 className="text-xl font-black uppercase tracking-tight text-foreground">Textos de Marketing</h3>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-3">
                      <Label className="text-xs font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                        Título Principal <Badge variant="outline" className="text-[9px] py-0">Sync Global</Badge>
                      </Label>
                      <Input 
                        value={promoConfig.titulo} 
                        onChange={(e) => setPromoConfig({...promoConfig, titulo: e.target.value})}
                        placeholder="Ej: Participa por tu iPhone hoy"
                        className="h-12 rounded-xl dark:bg-white/5 font-bold"
                      />
                    </div>
                    <div className="space-y-3">
                      <Label className="text-xs font-black uppercase tracking-widest text-muted-foreground">
                        Texto Promocional (Badge/Tagline)
                      </Label>
                      <Input 
                        value={promoConfig.texto_promocional} 
                        onChange={(e) => setPromoConfig({...promoConfig, texto_promocional: e.target.value})}
                        placeholder="Ej: Últimos boletos disponibles"
                        className="h-12 rounded-xl dark:bg-white/5"
                      />
                    </div>
                    <div className="md:col-span-2 space-y-3">
                      <Label className="text-xs font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                        Descripción Detallada <Badge variant="outline" className="text-[9px] py-0">Sync Global</Badge>
                      </Label>
                      <textarea 
                        value={promoConfig.descripcion}
                        onChange={(e) => setPromoConfig({...promoConfig, descripcion: e.target.value})}
                        className="w-full min-h-[120px] p-4 rounded-2xl dark:bg-black/40 border border-slate-200 dark:border-white/10 focus:ring-2 focus:ring-orange-500/20 text-sm transition-all text-foreground outline-none resize-none"
                        placeholder="Describe los beneficios y el premio de forma atractiva..."
                      />
                    </div>
                  </div>
                </div>

                {/* SECCIÓN 3: GALERÍA MULTIMEDIA (MAX 3) */}
                <div className="space-y-6">
                  <div className="flex items-center justify-between border-l-4 border-blue-500 pl-4 py-1">
                    <div className="flex items-center gap-3">
                      <Layout className="w-5 h-5 text-blue-500" />
                      <h3 className="text-xl font-black uppercase tracking-tight text-foreground">Galería Multimedia</h3>
                    </div>
                    <Badge variant="outline" className="px-3 font-bold border-blue-500/30 text-blue-500">{promoConfig.multimedia.length}/3 Elementos</Badge>
                  </div>

                  <div className="grid md:grid-cols-3 gap-6">
                    {promoConfig.multimedia.map((item, idx) => {
                      // Limpiar el /api de la URL base para las imágenes estáticas
                      const baseUrl = API?.replace('/api', '') || '';
                      let previewUrl = item.url;
                      
                      if (item.tipo === 'video' || item.platform === 'youtube') {
                        const vidId = item.url.match(/(?:youtu\.be\/|youtube\.com\/(?:v\/|u\/\w\/|embed\/|watch\?v=))([^#&?]*)/)?.[1];
                        if (vidId) previewUrl = `https://img.youtube.com/vi/${vidId}/mqdefault.jpg`;
                      } else if (!item.url.startsWith('http')) {
                        previewUrl = `${baseUrl}${item.url}`;
                      }

                      return (
                        <div key={item.id} className="group relative p-4 bg-slate-50 dark:bg-white/5 rounded-3xl border border-slate-200 dark:border-white/10 hover:border-blue-500/50 transition-all duration-300 shadow-sm">
                          <div className="aspect-video w-full rounded-2xl overflow-hidden bg-slate-200 dark:bg-white/10 mb-4 border dark:border-white/5 relative">
                            <img 
                              src={previewUrl} 
                              className="w-full h-full object-cover" 
                              alt="Preview" 
                              onError={(e) => {
                                if (item.tipo === 'video') {
                                   e.target.style.display = 'none';
                                }
                              }}
                            />
                            {item.tipo === 'video' && (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                <Youtube className="w-8 h-8 text-red-500 shadow-2xl" />
                              </div>
                            )}
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button 
                              onClick={() => {
                                const newMulti = promoConfig.multimedia.filter(m => m.id !== item.id);
                                setPromoConfig({...promoConfig, multimedia: newMulti});
                              }}
                              className="w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
                            >
                              <Trash className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex gap-2">
                             <Badge variant={item.tipo === 'image' ? 'secondary' : 'default'} className="uppercase text-[9px] px-2 py-0.5">{item.tipo}</Badge>
                             <Badge variant="ghost" className="uppercase text-[9px] px-2 py-0.5">{item.platform || 'manual'}</Badge>
                          </div>
                          <Input 
                            readOnly 
                            value={item.url.substring(0, 30) + '...'} 
                            className="h-8 text-[10px] bg-transparent border-slate-200 dark:border-white/5"
                          />
                        </div>
                      </div>
                      );
                    })}

                    {/* SLOT DE CARGA SI HAY MENOS DE 3 */}
                    {promoConfig.multimedia.length < 3 && (
                      <div className="flex flex-col gap-3 p-6 bg-blue-50/30 dark:bg-blue-500/5 rounded-3xl border-2 border-dashed border-blue-500/30 hover:border-blue-500/60 transition-all group">
                        <p className="text-xs font-black uppercase text-blue-500 text-center mb-2 tracking-tighter">Añadir Medio {promoConfig.multimedia.length + 1}</p>
                        
                        <div className="space-y-4">
                           {/* OPCIÓN 1: SUBIR IMAGEN */}
                           <div className="relative overflow-hidden group/btn">
                             <input 
                               type="file" 
                               accept="image/*"
                               className="absolute inset-0 opacity-0 cursor-pointer z-10"
                               onChange={async (e) => {
                                 const file = e.target.files[0];
                                 if (!file) return;
                                 const formData = new FormData();
                                 formData.append('file', file);
                                 try {
                                   toast.loading('Subiendo imagen...');
                                   const res = await axios.post(`${API}/admin/sorteo/${sorteo.id}/upload-promo-media`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                                   const newItem = { id: Date.now().toString(), tipo: 'image', url: res.data.url, platform: 'local' };
                                   setPromoConfig({...promoConfig, multimedia: [...promoConfig.multimedia, newItem]});
                                   toast.dismiss();
                                   toast.success('Imagen agregada');
                                 } catch (err) { toast.dismiss(); toast.error('Error al subir'); }
                               }}
                             />
                             <div className="flex items-center gap-2 px-4 py-3 bg-white dark:bg-white/10 group-hover/btn:bg-blue-500 group-hover/btn:text-white rounded-xl border border-blue-500/20 transition-all font-bold text-[10px] uppercase justify-center">
                               <UploadCloud className="w-4 h-4" /> Subir Imagen Local
                             </div>
                           </div>

                           <div className="relative">
                              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-blue-500/10"></span></div>
                              <div className="relative flex justify-center"><span className="bg-blue-50 dark:bg-[#151921] px-2 text-[9px] text-blue-400 font-bold uppercase">O Video URL</span></div>
                           </div>

                           <div className="flex gap-2">
                              <Input 
                                id="video-url-input"
                                placeholder="Link YouTube/TikTok"
                                className="h-10 text-[10px] dark:bg-white/5 border-blue-500/20"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    const url = e.target.value;
                                    if (!url) return;
                                    let platform = null;
                                    if (url.includes('youtube.com') || url.includes('youtu.be')) platform = 'youtube';
                                    else if (url.includes('tiktok.com')) platform = 'tiktok';
                                    
                                    if (!platform) {
                                      toast.error("URL no compatible (Youtube/TikTok)");
                                      return;
                                    }

                                    const newItem = { id: Date.now().toString(), tipo: 'video', url, platform };
                                    setPromoConfig({...promoConfig, multimedia: [...promoConfig.multimedia, newItem]});
                                    e.target.value = '';
                                    toast.success("Video agregado");
                                  }
                                }}
                              />
                              <Button 
                                size="icon" className="h-10 w-10 bg-blue-500 hover:bg-blue-600 text-white flex-shrink-0"
                                onClick={() => {
                                  const input = document.getElementById('video-url-input');
                                  const url = input.value;
                                  if (!url) return;
                                  let platform = null;
                                  if (url.includes('youtube.com') || url.includes('youtu.be')) platform = 'youtube';
                                  else if (url.includes('tiktok.com')) platform = 'tiktok';
                                  
                                  if (!platform) {
                                    toast.error("Solo YouTube/TikTok son compatibles por ahora");
                                    return;
                                  }

                                  const newItem = { id: Date.now().toString(), tipo: 'video', url, platform };
                                  setPromoConfig({...promoConfig, multimedia: [...promoConfig.multimedia, newItem]});
                                  input.value = '';
                                  toast.success("Video agregado correctamente");
                                }}
                              >
                                <Plus className="w-5 h-5" />
                              </Button>
                           </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                  {/* SECCIÓN 4: MEDIA DE PREMIOS (MANTENER FUNCIONALIDAD) */}
                  <div className="pt-8 border-t border-slate-100 dark:border-white/5 space-y-10">
                    
                    <div className="flex items-center justify-between border-l-4 border-orange-500 pl-4 py-1">
                      <div className="flex items-center gap-3">
                        <Trophy className="w-5 h-5 text-orange-500" />
                        <h3 className="text-xl font-black uppercase tracking-tight text-foreground">Premios del Sorteo</h3>
                      </div>
                      <Badge variant="outline" className="px-3 font-bold border-orange-500/30 text-orange-500">
                        {sorteo.tipo === 'etapas' ? `${sorteo.etapas?.length} Etapas` : `${sorteo.premios?.length} Premios`}
                      </Badge>
                    </div>

                    {/* UNICO */}
                    {sorteo.tipo === 'unico' && sorteo.premios?.map((p, idx) => (
                      <div key={idx} className="space-y-6">
                         <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center text-white text-xs font-black italic shadow-lg shadow-orange-500/30">
                              {idx + 1}
                            </div>
                            <h4 className="text-lg font-black uppercase tracking-tighter italic">{p.nombre}</h4>
                         </div>

                         <div className="grid md:grid-cols-2 gap-6">
                            {/* CARD IMAGEN */}
                            <div className="p-5 bg-slate-50 dark:bg-white/5 rounded-3xl border border-slate-200 dark:border-white/10 space-y-4 hover:border-orange-500/30 transition-all shadow-sm">
                               <div className="flex items-center justify-between">
                                 <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-2">Fotografía del Premio</Label>
                                 <Badge variant="secondary" className="bg-orange-500/10 text-orange-600 dark:text-orange-400 text-[8px] font-black italic uppercase">Imagen</Badge>
                               </div>
                               
                               <div className="aspect-video w-full rounded-2xl overflow-hidden bg-slate-200 dark:bg-black/40 border dark:border-white/5 relative group/prize shadow-inner">
                                  <img 
                                    src={getMediaUrl(premiosImagenesTemp[`p-${idx}-img`] !== undefined ? premiosImagenesTemp[`p-${idx}-img`] : p.imagen_url)} 
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover/prize:scale-110" 
                                    alt="Premio" 
                                  />
                                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/prize:opacity-100 transition-all duration-300 flex items-center justify-center backdrop-blur-[2px]">
                                     <div className="relative">
                                        <input 
                                          type="file" 
                                          className="absolute inset-0 opacity-0 cursor-pointer z-10" 
                                          onChange={(e) => handlePrizeFileUpload(idx, null, e.target.files[0])} 
                                        />
                                        <Button size="sm" variant="secondary" className="gap-2 pointer-events-none shadow-xl">
                                           <UploadCloud className="w-4 h-4" /> Cambiar Imagen
                                        </Button>
                                     </div>
                                  </div>
                               </div>

                               <div className="relative">
                                  <Input 
                                    className="h-10 pr-10 text-xs rounded-xl dark:bg-black/40 border-slate-200 dark:border-white/5 italic focus:ring-orange-500/20" 
                                    placeholder="URL de imagen externa..."
                                    value={premiosImagenesTemp[`p-${idx}-img`] !== undefined ? premiosImagenesTemp[`p-${idx}-img`] : (p.imagen_url || '')} 
                                    onChange={e => setPremiosImagenesTemp(prev => ({...prev, [`p-${idx}-img`]: e.target.value}))} 
                                  />
                                  <button className="absolute right-3 top-2.5 text-slate-400 hover:text-orange-500 transition-colors" onClick={async () => {
                                     try { await axios.put(`${API}/admin/sorteo/${sorteo.id}/actualizar-premio-imagen`, { premio_index: idx, imagen_url: premiosImagenesTemp[`p-${idx}-img`] || p.imagen_url }); toast.success('Ok'); fetchData(); } catch(e){toast.error('Error');}
                                  }} title="Guardar URL"><Save className="w-4 h-4" /></button>
                               </div>
                            </div>

                            {/* CARD VIDEO */}
                            <div className="p-5 bg-slate-50 dark:bg-white/5 rounded-3xl border border-slate-200 dark:border-white/10 space-y-4 hover:border-orange-500/30 transition-all shadow-sm">
                               <div className="flex items-center justify-between">
                                 <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-2">Video de Apoyo</Label>
                                 <Badge className="bg-blue-500/10 text-blue-600 dark:text-blue-400 text-[8px] font-black italic uppercase">Video / Youtube</Badge>
                               </div>

                               <div className="aspect-video w-full rounded-2xl overflow-hidden bg-slate-200 dark:bg-black/40 border dark:border-white/5 relative group/vid shadow-inner flex items-center justify-center">
                                  {getYoutubePreview(premiosImagenesTemp[`p-${idx}-vid`] !== undefined ? premiosImagenesTemp[`p-${idx}-vid`] : p.video_url) ? (
                                     <>
                                       <img 
                                         src={getYoutubePreview(premiosImagenesTemp[`p-${idx}-vid`] !== undefined ? premiosImagenesTemp[`p-${idx}-vid`] : p.video_url)} 
                                         className="w-full h-full object-cover opacity-80" 
                                         alt="Video Preview" 
                                       />
                                       <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/20 group-hover/vid:bg-black/40 transition-all">
                                         <div className="w-12 h-12 rounded-full bg-red-600 text-white flex items-center justify-center shadow-2xl scale-90 group-hover/vid:scale-100 transition-transform">
                                            <Play className="w-6 h-6 fill-current" />
                                         </div>
                                         <span className="mt-2 text-[10px] font-black text-white uppercase tracking-widest opacity-0 group-hover/vid:opacity-100 transition-opacity">Vista Previa Active</span>
                                       </div>
                                     </>
                                  ) : (
                                     <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-slate-400">
                                        <div className="w-10 h-10 rounded-xl bg-slate-300/30 dark:bg-white/5 flex items-center justify-center">
                                          <Video className="w-6 h-6" />
                                        </div>
                                        <span className="text-[10px] font-black uppercase tracking-widest opacity-60">Configurar Video URL</span>
                                     </div>
                                  )}
                               </div>

                               <div className="relative">
                                  <Input 
                                    className="h-10 pr-10 text-xs rounded-xl dark:bg-black/40 border-slate-200 dark:border-white/5 italic focus:ring-orange-500/20" 
                                    placeholder="Link de YouTube del producto..."
                                    value={premiosImagenesTemp[`p-${idx}-vid`] !== undefined ? premiosImagenesTemp[`p-${idx}-vid`] : (p.video_url || '')} 
                                    onChange={e => setPremiosImagenesTemp(prev => ({...prev, [`p-${idx}-vid`]: e.target.value}))} 
                                  />
                                  <button className="absolute right-3 top-2.5 text-slate-400 hover:text-orange-500 transition-colors" onClick={async () => {
                                     try { await axios.put(`${API}/admin/sorteo/${sorteo.id}/actualizar-premio-video`, { premio_index: idx, video_url: premiosImagenesTemp[`p-${idx}-vid`] || p.video_url }); toast.success('Ok'); fetchData(); } catch(e){toast.error('Error');}
                                  }} title="Guardar URL"><Save className="w-4 h-4" /></button>
                               </div>
                            </div>
                         </div>
                      </div>
                    ))}

                    {/* ETAPAS */}
                    {sorteo.tipo === 'etapas' && sorteo.etapas?.map((et, eIdx) => (
                      <div key={eIdx} className="space-y-8">
                         <div className="flex items-center gap-3 p-4 bg-slate-950/5 dark:bg-white/5 rounded-2xl border-l-4 border-orange-500">
                           <Layout className="w-5 h-5 text-orange-500" />
                           <h4 className="text-sm font-black uppercase tracking-widest italic">Etapa {et.numero}: {et.nombre || et.premio}</h4>
                         </div>
                         
                         <div className="space-y-12 ml-6">
                          {et.premios?.map((p, pIdx) => (
                            <div key={pIdx} className="space-y-6">
                               <p className="text-base font-black uppercase tracking-tight italic text-orange-600 dark:text-orange-500">{pIdx + 1}. {p.nombre}</p>
                               <div className="grid md:grid-cols-2 gap-6">
                                 {/* CARD IMAGEN ETAPA */}
                                 <div className="p-5 bg-slate-50 dark:bg-white/5 rounded-3xl border border-slate-200 dark:border-white/10 space-y-4 hover:border-orange-500/30 transition-all shadow-sm">
                                    <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-2">Imagen Etapa</Label>
                                    <div className="aspect-video w-full rounded-2xl overflow-hidden bg-slate-200 dark:bg-black/40 border dark:border-white/5 relative group/prize shadow-inner">
                                       <img src={getMediaUrl(premiosImagenesTemp[`e-${eIdx}-p-${pIdx}-img`] !== undefined ? premiosImagenesTemp[`e-${eIdx}-p-${pIdx}-img`] : p.imagen_url)} className="w-full h-full object-cover transition-transform duration-500 group-hover/prize:scale-110" alt="Premio" />
                                       <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/prize:opacity-100 transition-all duration-300 flex items-center justify-center backdrop-blur-[2px]">
                                          <div className="relative">
                                             <input type="file" className="absolute inset-0 opacity-0 cursor-pointer z-10" onChange={(e) => handlePrizeFileUpload(pIdx, eIdx, e.target.files[0])} />
                                             <Button size="sm" variant="secondary" className="gap-2 pointer-events-none shadow-xl">
                                                <UploadCloud className="w-4 h-4" /> Cambiar Imagen
                                             </Button>
                                          </div>
                                       </div>
                                    </div>
                                    <div className="relative">
                                       <Input className="h-10 pr-10 text-xs rounded-xl dark:bg-black/40 border-slate-200 dark:border-white/5 italic focus:ring-orange-500/20" value={premiosImagenesTemp[`e-${eIdx}-p-${pIdx}-img`] !== undefined ? premiosImagenesTemp[`e-${eIdx}-p-${pIdx}-img`] : (p.imagen_url || '')} onChange={e => setPremiosImagenesTemp(prev => ({...prev, [`e-${eIdx}-p-${pIdx}-img`]: e.target.value}))} />
                                       <button className="absolute right-3 top-2.5 text-slate-400 hover:text-orange-500 transition-colors" onClick={async () => {
                                         try { await axios.put(`${API}/admin/sorteo/${sorteo.id}/actualizar-premio-imagen`, { etapa_index: eIdx, premio_index: pIdx, imagen_url: premiosImagenesTemp[`e-${eIdx}-p-${pIdx}-img`] || p.imagen_url }); toast.success('Ok'); fetchData(); } catch(e){toast.error('Error');}
                                       }}><Save className="w-4 h-4" /></button>
                                    </div>
                                 </div>

                                 {/* CARD VIDEO ETAPA */}
                                 <div className="p-5 bg-slate-50 dark:bg-white/5 rounded-3xl border border-slate-200 dark:border-white/10 space-y-4 hover:border-orange-500/30 transition-all shadow-sm">
                                    <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-2">Video Etapa</Label>
                                    <div className="aspect-video w-full rounded-2xl overflow-hidden bg-slate-200 dark:bg-black/40 border dark:border-white/5 relative group/vid shadow-inner flex items-center justify-center">
                                       {getYoutubePreview(premiosImagenesTemp[`e-${eIdx}-p-${pIdx}-vid`] !== undefined ? premiosImagenesTemp[`e-${eIdx}-p-${pIdx}-vid`] : p.video_url) ? (
                                          <>
                                            <img src={getYoutubePreview(premiosImagenesTemp[`e-${eIdx}-p-${pIdx}-vid`] !== undefined ? premiosImagenesTemp[`e-${eIdx}-p-${pIdx}-vid`] : p.video_url)} className="w-full h-full object-cover opacity-80" alt="Video Preview" />
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover/vid:bg-black/40 transition-all">
                                              <div className="w-12 h-12 rounded-full bg-red-600 text-white flex items-center justify-center shadow-2xl scale-90 group-hover/vid:scale-100 transition-transform">
                                                 <Play className="w-6 h-6 fill-current" />
                                              </div>
                                            </div>
                                          </>
                                       ) : (
                                          <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-slate-400">
                                             <Video className="w-6 h-6" />
                                             <span className="text-[10px] font-black uppercase tracking-widest opacity-60">Configurar Video</span>
                                          </div>
                                       )}
                                    </div>
                                    <div className="relative">
                                       <Input className="h-10 pr-10 text-xs rounded-xl dark:bg-black/40 border-slate-200 dark:border-white/5 italic focus:ring-orange-500/20" value={premiosImagenesTemp[`e-${eIdx}-p-${pIdx}-vid`] !== undefined ? premiosImagenesTemp[`e-${eIdx}-p-${pIdx}-vid`] : (p.video_url || '')} onChange={e => setPremiosImagenesTemp(prev => ({...prev, [`e-${eIdx}-p-${pIdx}-vid`]: e.target.value}))} />
                                       <button className="absolute right-3 top-2.5 text-slate-400 hover:text-orange-500 transition-colors" onClick={async () => {
                                         try { await axios.put(`${API}/admin/sorteo/${sorteo.id}/actualizar-premio-video`, { etapa_index: eIdx, premio_index: pIdx, video_url: premiosImagenesTemp[`e-${eIdx}-p-${pIdx}-vid`] || p.video_url }); toast.success('Ok'); fetchData(); } catch(e){toast.error('Error');}
                                       }}><Save className="w-4 h-4" /></button>
                                    </div>
                                 </div>
                               </div>
                            </div>
                          ))}
                         </div>
                      </div>
                    ))}
                  </div>

              <div className="px-10 py-8 border-t border-slate-100 dark:border-white/5 bg-slate-50/50 dark:bg-white/5 flex items-center justify-between">
                <div className="text-xs text-muted-foreground flex items-center gap-2">
                   <Info className="w-4 h-4 text-orange-500" /> Los cambios en título y descripción afectan todas las vistas.
                </div>
                <div className="flex gap-4">
                  <AppButton variant="secondary" onClick={() => setShowMediosModal(false)}>Descartar Cambios</AppButton>
                  <AppButton 
                    variant="primary" 
                    loading={isSavingPromo}
                    onClick={async () => {
                       setIsSavingPromo(true);
                       try {
                         await axios.put(`${API}/admin/sorteo/${sorteo.id}/configuracion-promocional`, promoConfig);
                         toast.success('Campaña Publicitaria Guardada Correctamente');
                         fetchData();
                         setShowMediosModal(false);
                       } catch (e) { toast.error('Error al guardar la campaña'); }
                       finally { setIsSavingPromo(false); }
                    }}
                    className="px-8 shadow-xl shadow-orange-500/20"
                    icon={Save}
                  >
                    Guardar Configuración Home
                  </AppButton>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

          {/* MODAL DE GESTIÓN DE PAQUETES */}
          <Dialog open={showPaquetesModal} onOpenChange={setShowPaquetesModal}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0 border-none shadow-2xl rounded-[2rem]">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-orange-400 via-orange-600 to-orange-400"></div>
              
              <DialogHeader className="px-8 pt-8 pb-4 border-b bg-slate-50/50 dark:bg-white/5">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-orange-500 flex items-center justify-center text-white shadow-lg shadow-orange-500/20">
                    <Plus className="w-6 h-6" />
                  </div>
                  <div>
                    <DialogTitle className="text-2xl font-black tracking-tight text-foreground">Sistemas de Paquetes</DialogTitle>
                    <DialogDescription>Configura ofertas dinámicas de boletos con descuentos</DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto p-8 space-y-8">
                {/* Formulario Nuevo Paquete */}
                <div className="p-6 bg-orange-50 dark:bg-orange-500/5 rounded-3xl border border-orange-200 dark:border-orange-500/20">
                  <h4 className="text-sm font-black uppercase text-orange-600 mb-4 flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Configurar Oferta Pro
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[10px] font-black uppercase tracking-widest">Cant. Base (Paga)</Label>
                      <Input 
                        type="number" 
                        value={nuevoPaquete.cantidad} 
                        onChange={(e) => setNuevoPaquete({...nuevoPaquete, cantidad: parseInt(e.target.value) || 0})}
                        className="h-11 rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[10px] font-black uppercase tracking-widest text-orange-500">Boletos de Regalo</Label>
                      <Input 
                        type="number" 
                        value={nuevoPaquete.boletos_gratis} 
                        onChange={(e) => setNuevoPaquete({...nuevoPaquete, boletos_gratis: parseInt(e.target.value) || 0})}
                        className="h-11 rounded-xl border-orange-500/30"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[10px] font-black uppercase tracking-widest">Descuento (%)</Label>
                      <Input 
                        type="number" 
                        value={nuevoPaquete.descuento} 
                        onChange={(e) => setNuevoPaquete({...nuevoPaquete, descuento: parseFloat(e.target.value) || 0})}
                        className="h-11 rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[10px] font-black uppercase tracking-widest">Etiqueta (opcional)</Label>
                      <Input 
                        placeholder="Ej: MÁS VENDIDO"
                        value={nuevoPaquete.etiqueta} 
                        onChange={(e) => setNuevoPaquete({...nuevoPaquete, etiqueta: e.target.value.toUpperCase()})}
                        className="h-11 rounded-xl"
                      />
                    </div>
                    <div className="flex items-end">
                      <AppButton 
                        className="w-full h-11 rounded-xl shadow-lg shadow-orange-500/20"
                        onClick={() => {
                          if (nuevoPaquete.cantidad <= 0) return toast.error("La cantidad base debe ser mayor a 0");
                          const id = Math.random().toString(36).substr(2, 9);
                          setPaquetesEdit([...paquetesEdit, { ...nuevoPaquete, id }]);
                          setNuevoPaquete({ cantidad: 10, boletos_gratis: 0, descuento: 0, etiqueta: '', activo: true });
                          toast.success("Paquete añadido a la lista");
                        }}
                      >
                        Crear Paquete
                      </AppButton>
                    </div>
                  </div>

                  {/* PREVIEW DINAMICO */}
                  <div className="mt-6 pt-6 border-t border-orange-200 dark:border-orange-500/20">
                    <div className="bg-white/50 dark:bg-black/20 p-4 rounded-2xl border border-dashed border-orange-300 dark:border-orange-500/30">
                       <div className="flex flex-wrap items-center justify-between gap-4">
                          <div className="flex gap-8">
                             <div>
                               <span className="block text-[8px] font-black uppercase text-muted-foreground mb-1">Inversión Usuario</span>
                               <span className="text-sm font-bold">{formatCurrency((nuevoPaquete.cantidad * sorteo.precio_boleto) * (1 - (nuevoPaquete.descuento / 100)))}</span>
                             </div>
                             <div>
                               <span className="block text-[8px] font-black uppercase text-muted-foreground mb-1">Recibe Total</span>
                               <span className="text-sm font-bold text-orange-500">{nuevoPaquete.cantidad + (nuevoPaquete.boletos_gratis || 0)} Boletos</span>
                             </div>
                             <div>
                               <span className="block text-[8px] font-black uppercase text-muted-foreground mb-1">Ahorro Percibido</span>
                               <span className="text-sm font-bold text-green-500">
                                 {formatCurrency((nuevoPaquete.cantidad * sorteo.precio_boleto) * (nuevoPaquete.descuento / 100) + ((nuevoPaquete.boletos_gratis || 0) * sorteo.precio_boleto))}
                               </span>
                             </div>
                          </div>
                          <div className="px-4 py-2 bg-orange-500 text-white rounded-xl text-[10px] font-black uppercase tracking-tighter">
                            Oferta: {nuevoPaquete.descuento > 0 ? `${nuevoPaquete.descuento}% OFF` : ''} 
                            {nuevoPaquete.descuento > 0 && nuevoPaquete.boletos_gratis > 0 ? ' + ' : ''}
                            {nuevoPaquete.boletos_gratis > 0 ? `${nuevoPaquete.boletos_gratis} GRATIS` : ''}
                            {nuevoPaquete.descuento === 0 && nuevoPaquete.boletos_gratis === 0 ? 'Normal' : ''}
                          </div>
                       </div>
                    </div>
                  </div>
                </div>

                {/* Lista de Paquetes */}
                <div className="space-y-4">
                  <h4 className="text-sm font-black uppercase tracking-widest text-muted-foreground border-b pb-2">Paquetes Configurados</h4>
                  {paquetesEdit.length === 0 ? (
                    <div className="py-12 text-center bg-slate-50 dark:bg-white/5 rounded-3xl border-2 border-dashed">
                      <p className="text-sm text-muted-foreground font-medium">No hay paquetes configurados aún.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {paquetesEdit.map((p, idx) => (
                        <div key={p.id} className="p-5 bg-white dark:bg-white/5 rounded-3xl border border-slate-200 dark:border-white/10 flex items-center justify-between group hover:border-orange-500/50 transition-all duration-300">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-orange-500 flex flex-col items-center justify-center text-white shadow-lg shadow-orange-500/10">
                              <span className="text-lg font-black">{p.cantidad + (p.boletos_gratis || 0)}</span>
                              <span className="text-[7px] font-black opacity-80 uppercase tracking-tighter">TOTAL</span>
                            </div>
                            <div>
                               <div className="flex items-center gap-2">
                                 <span className="font-black text-base">{p.cantidad} <span className="text-[10px] font-bold text-muted-foreground">PAGADOS</span></span>
                                 {p.etiqueta && <Badge className="bg-orange-500 text-[8px] font-black">{p.etiqueta}</Badge>}
                               </div>
                               <p className="text-[10px] font-bold text-orange-500 uppercase flex items-center gap-1">
                                 {p.boletos_gratis > 0 && <><Plus className="w-3 h-3" /> {p.boletos_gratis} DE REGALO</>}
                                 {p.descuento > 0 && <><ArrowRight className="w-3 h-3 mx-1" /> {p.descuento}% OFF</>}
                               </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <AppButton 
                              variant="ghost" 
                              size="sm" 
                              className="h-10 w-10 p-0 text-red-500 hover:bg-red-500/10 rounded-xl"
                              onClick={() => setPaquetesEdit(paquetesEdit.filter(item => item.id !== p.id))}
                            >
                              <Trash className="w-4 h-4" />
                            </AppButton>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="p-8 border-t bg-slate-50/50 flex items-center justify-between">
                <div className="text-xs text-muted-foreground italic flex items-center gap-2">
                   <Info className="w-4 h-4 text-orange-500" /> Los paquetes aparecerán debajo del CTA en el Home.
                </div>
                <div className="flex gap-4">
                  <AppButton variant="secondary" onClick={() => setShowPaquetesModal(false)}>Cancelar</AppButton>
                  <AppButton 
                    variant="primary" 
                    loading={isSavingPaquetes}
                    onClick={async () => {
                      setIsSavingPaquetes(true);
                      try {
                        await axios.put(`${API}/admin/sorteo/${sorteo.id}/paquetes`, paquetesEdit);
                        toast.success('Paquetes actualizados exitosamente');
                        fetchData();
                        setShowPaquetesModal(false);
                      } catch (e) {
                        toast.error('Error al guardar los paquetes');
                      } finally {
                        setIsSavingPaquetes(false);
                      }
                    }}
                    icon={Save}
                    className="px-10 shadow-xl shadow-orange-500/20"
                  >
                    Guardar Paquetes
                  </AppButton>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardContent>
    </Card>
  );
};

export default RaffleCard;
